import { useEffect, useState } from "react";
import { useRequest } from "../hooks/useRequest";
import {
  getOrderDetail,
  OrderDetailItem,
  OrderPackageProgress,
  OrderShipmentSegment,
} from "../api/orders";
import toast from "react-hot-toast";
import { formatCents, formatDateTime } from "../utils/format";
import "./OrderDetailDrawer.scss";

/** 空值占位 */
const DASH = "—";

/** 物流状态枚举 → 展示文案；未知则原样显示 */
const PKG_STATUS_LABELS: Record<string, string> = {
  EN_ROUTE: "In Transit",
  DELIVERED: "Delivered",
  PENDING: "Pending",
  PICKED_UP: "Picked Up",
  OUT_FOR_DELIVERY: "Out for delivery",
  EXCEPTION: "Exception",
  CREATED: "Created",
};

function pkgStatusLabel(status: string | null): string {
  if (!status) return "";
  return PKG_STATUS_LABELS[status.toUpperCase()] || status;
}

/** 组合轨迹节点标题：activity + location（去重后用 · 连接） */
function progressTitle(activity: string | null, location: string | null): string {
  const parts = [activity, location].filter(Boolean) as string[];
  const uniq = parts.filter((p, i) => parts.indexOf(p) === i);
  return uniq.join(" · ") || DASH;
}

interface OrderDetailDrawerProps {
  /** 要展示详情的订单号（组件仅在有订单号时由父级挂载） */
  orderId: string;
  /** 关闭弹窗回调 */
  onClose: () => void;
}

const renderAmount = (cents: number | null): string =>
  cents == null ? DASH : formatCents(cents);

const renderDateTime = (iso: string | null): string =>
  iso ? formatDateTime(iso) : DASH;

/** 复制到剪贴板，成功后弹出 toast 提示 */
function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Failed to copy"));
  }
}

/** 单条商品卡片（主视图） */
const ItemCard = ({
  item,
  onViewTracking,
}: {
  item: OrderDetailItem;
  onViewTracking: () => void;
}) => {
  const captions = [item.brand, item.size, item.color]
    .filter(Boolean)
    .join(" · ");
  const shipmentCount = item.segments.length;
  // 取所有分段中最新的一个轨迹节点作为概览
  const latest = item.segments
    .flatMap((s) => s.progresses)
    .filter((p) => p.timestamp)
    .slice(-1)[0];

  return (
    <div className="cs-od__item">
      <div className="cs-od__item-top">
        <div
          className="cs-od__item-thumb"
          style={
            item.imageUrl
              ? { backgroundImage: `url(${item.imageUrl})` }
              : undefined
          }
        />
        <div className="cs-od__item-info">
          <div className="cs-od__item-name">{item.name || DASH}</div>
          {captions && <div className="cs-od__item-size">{captions}</div>}
        </div>
        <div className="cs-od__item-qty">× {item.quantity}</div>
      </div>

      {shipmentCount === 0 ? (
        <div className="cs-od__item-noship">No shipping info</div>
      ) : (
        <div className="cs-od__item-ship">
          <div className="cs-od__ship-summary">
            <span className="cs-od__ship-count">
              {shipmentCount > 1 ? `${shipmentCount} shipments` : "1 shipment"}
            </span>
            {latest && (
              <span className="cs-od__ship-latest">
                {progressTitle(latest.activity, latest.location)}
              </span>
            )}
          </div>
          <button
            type="button"
            className="cs-od__tracking"
            onClick={onViewTracking}
          >
            View Tracking ›
          </button>
        </div>
      )}
    </div>
  );
};

/** 单段物流的轨迹时间线 */
const Timeline = ({
  progresses,
  delivered,
}: {
  progresses: OrderPackageProgress[];
  delivered: boolean;
}) => {
  if (progresses.length === 0) {
    return <div className="cs-od__state">No tracking updates yet.</div>;
  }
  return (
    <ul className="cs-od__tl">
      {progresses.map((p, i) => {
        const isLast = i === progresses.length - 1;
        const success = isLast && delivered;
        return (
          <li
            key={i}
            className={`cs-od__tl-node${isLast ? " cs-od__tl-node--last" : ""}${
              success ? " cs-od__tl-node--success" : ""
            }`}
          >
            <span className="cs-od__tl-dot" />
            <div className="cs-od__tl-body">
              <div className="cs-od__tl-title">
                {progressTitle(p.activity, p.location)}
              </div>
              {p.timestamp && (
                <div className="cs-od__tl-time">{renderDateTime(p.timestamp)}</div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/** 单段物流区块（Shipment i of N），每段独立展示自己的包裹号与轨迹 */
const ShipmentBlock = ({
  segment,
  index,
  total,
}: {
  segment: OrderShipmentSegment;
  index: number;
  total: number;
}) => (
  <section className="cs-od__shipment">
    <h4 className="cs-od__shipment-title">
      Shipment {index + 1} of {total}
    </h4>

    <dl className="cs-od__grid cs-od__leg">
      <dt>Carrier</dt>
      <dd>{segment.carrierName || segment.carrier || DASH}</dd>
      <dt>Tracking #</dt>
      <dd>
        {segment.trackingNumber ? (
          <span className="cs-od__track-no">
            <span className="cs-od__track-no-text">{segment.trackingNumber}</span>
            <button
              type="button"
              className="cs-od__copy"
              onClick={() => copyText(segment.trackingNumber as string)}
            >
              Copy
            </button>
          </span>
        ) : (
          DASH
        )}
      </dd>
    </dl>

    <Timeline progresses={segment.progresses} delivered={segment.delivered} />
  </section>
);

/** Item Details 视图：展示该商品的所有发货 + 聚合轨迹 */
const ItemDetailView = ({
  item,
  onBack,
}: {
  item: OrderDetailItem;
  onBack: () => void;
}) => (
  <div className="cs-od__item-detail">
    <div className="cs-od__item-top cs-od__item-top--detail">
      <div
        className="cs-od__item-thumb"
        style={
          item.imageUrl ? { backgroundImage: `url(${item.imageUrl})` } : undefined
        }
      />
      <div className="cs-od__item-info">
        <div className="cs-od__item-name">{item.name || DASH}</div>
        {item.size && <div className="cs-od__item-size">size: {item.size}</div>}
      </div>
    </div>

    {item.segments.length === 0 ? (
      <div className="cs-od__state">No shipping info.</div>
    ) : (
      item.segments.map((s, i) => (
        <ShipmentBlock
          key={s.trackingNumber || i}
          segment={s}
          index={i}
          total={item.segments.length}
        />
      ))
    )}

    <button type="button" className="cs-od__back-btn" onClick={onBack}>
      ‹ Back
    </button>
  </div>
);

const OrderDetailDrawer = ({ orderId, onClose }: OrderDetailDrawerProps) => {
  // 当前查看物流详情的商品（null 表示展示订单主视图）
  const [activeItem, setActiveItem] = useState<OrderDetailItem | null>(null);

  // 抽屉打开期间锁定页面滚动（组件仅在打开时挂载，卸载即恢复）
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC 关闭：优先退出物流详情，其次关闭弹窗
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setActiveItem((cur) => {
        if (cur) return null;
        onClose();
        return null;
      });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { data, loading, error } = useRequest(
    () => getOrderDetail(orderId),
    [orderId]
  );

  const inDetail = activeItem != null;

  return (
    <div
      className="cs-od"
      role="dialog"
      aria-modal="true"
      aria-label="Order Details"
    >
      <div className="cs-od__backdrop" onClick={onClose} />
      <aside className="cs-od__panel">
        <header className="cs-od__header">
          <h2 className="cs-od__title">
            {inDetail ? "Item Details" : "Order Details"}
          </h2>
          <button
            type="button"
            className="cs-od__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="cs-od__body">
          {loading && <div className="cs-od__state">Loading…</div>}
          {!loading && error && (
            <div className="cs-od__state cs-od__state--error">{error}</div>
          )}

          {!loading && !error && data && inDetail && activeItem && (
            <ItemDetailView
              item={activeItem}
              onBack={() => setActiveItem(null)}
            />
          )}

          {!loading && !error && data && !inDetail && (
            <>
              {/* Order Info */}
              <section className="cs-od__section">
                <h3 className="cs-od__section-title">Order Info</h3>
                <dl className="cs-od__grid">
                  <dt>BXL Order ID</dt>
                  <dd>{data.orderId || DASH}</dd>
                  <dt>Merchant Order ID</dt>
                  <dd>{data.merchantOrderId || DASH}</dd>
                  <dt>Created At</dt>
                  <dd>{renderDateTime(data.createdAt)}</dd>
                </dl>
              </section>

              {/* Financial */}
              <section className="cs-od__section">
                <h3 className="cs-od__section-title">Financial</h3>
                <dl className="cs-od__grid">
                  <dt>Order Price</dt>
                  <dd className="cs-od__amount">
                    {renderAmount(data.orderPriceCents)}
                  </dd>
                  <dt>Actual Amount</dt>
                  <dd className="cs-od__amount">
                    {renderAmount(data.actualAmountCents)}
                  </dd>
                </dl>
              </section>

              {/* Items */}
              <section className="cs-od__section">
                <h3 className="cs-od__section-title">Items</h3>
                {data.items.length === 0 ? (
                  <div className="cs-od__state">No items.</div>
                ) : (
                  <div className="cs-od__items">
                    {data.items.map((it, i) => (
                      <ItemCard
                        key={i}
                        item={it}
                        onViewTracking={() => setActiveItem(it)}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default OrderDetailDrawer;
